import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Select,
  SortableTable,
  TabFilter,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
} from '@travellegacy/nexops-design-system';
import {
  ArrowLeft,
  CalendarClock,
  CalendarRange,
  List,
  Loader2,
  Pencil,
  Repeat,
  Square,
  TrendingUp,
} from 'lucide-react';
import { operationalCostsAPI } from '@/shared/lib/api';
import { formatCurrency } from '@/shared/lib/utils';
import {
  describeSchedule,
  FREQUENCY_BADGE_CLASSES,
  FREQUENCY_INTERVAL_MONTHS,
  FREQUENCY_LABELS,
  FREQUENCY_OPTIONS,
  FREQUENCY_PLURAL_LABELS,
  formatIsoDate,
  type RecurrenceFrequency,
} from '../utils/recurrence';

type OperationalCostCategory = 'TOOLS' | 'EVENTS' | 'OFFICE' | 'SERVICES' | 'MARKETING' | 'REFUNDS' | 'TAXES';
type RecurrenceStatus = 'ACTIVE' | 'ENDING' | 'ENDED';

interface RecurringCost {
  id: string;
  title: string;
  description: string | null;
  category: OperationalCostCategory;
  amount: number;
  paymentMethod: string | null;
  notes: string | null;
  frequency: RecurrenceFrequency;
  dayOfMonth: number;
  startDate: string;
  startAmount: number;
  endedAt: string | null;
  status: RecurrenceStatus;
  nextDueDate: string | null;
  launchesCount: number;
  monthlyEquivalent: number;
  annualized: number;
}

interface RecurringMetrics {
  activeCount: number;
  endedCount: number;
  monthlyEquivalentTotal: number;
  annualizedTotal: number;
  byFrequency: Record<RecurrenceFrequency, { count: number; amount: number; annualized: number }>;
}

interface Launch {
  id: string;
  paymentDate: string;
  amount: number;
}

const categoryLabels: Record<OperationalCostCategory, string> = {
  TOOLS: 'Ferramentas',
  EVENTS: 'Eventos',
  OFFICE: 'Escritório',
  SERVICES: 'Serviços',
  MARKETING: 'Marketing',
  REFUNDS: 'Prejuízos e Reembolsos',
  TAXES: 'Impostos e Encargos',
};

const categoryOptions = Object.entries(categoryLabels).map(([value, label]) => ({ value, label }));

function todayIso(): string {
  // en-CA formata como YYYY-MM-DD
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(new Date());
}

function formatCurrencyInput(value: string): string {
  const numbers = value.replace(/\D/g, '');
  if (!numbers) return '';
  return (parseInt(numbers) / 100).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function parseCurrencyInput(value: string): number {
  if (!value) return 0;
  return parseFloat(value.replace(/\./g, '').replace(',', '.')) || 0;
}

function getApiErrorMessage(error: unknown, fallback: string): string {
  const data = (error as { response?: { data?: { error?: { message?: string } } } })?.response
    ?.data;
  return data?.error?.message || fallback;
}

function MetricCard({
  title,
  value,
  subtitle,
  icon: Icon,
}: {
  title: string;
  value: string;
  subtitle: string;
  icon: React.ElementType;
}) {
  return (
    <Card>
      <CardContent className="py-5">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Icon className="h-4 w-4" />
          <span className="text-sm font-medium">{title}</span>
        </div>
        <div className="mt-2 text-2xl font-bold">{value}</div>
        <p className="mt-1 text-xs text-muted-foreground">{subtitle}</p>
      </CardContent>
    </Card>
  );
}

/**
 * Lançamentos de uma recorrência (próximos e anteriores)
 */
function LaunchesDialog({ cost, onClose }: { cost: RecurringCost | null; onClose: () => void }) {
  const { data: launches, isLoading } = useQuery({
    queryKey: ['operational-costs', 'children', cost?.id],
    queryFn: async () => {
      const response = await operationalCostsAPI.list({ parentCostId: cost!.id });
      return response.data.data as Launch[];
    },
    enabled: !!cost,
  });

  const today = todayIso();
  const all = cost
    ? [{ id: cost.id, paymentDate: cost.startDate, amount: cost.startAmount }, ...(launches ?? [])]
        .map((l) => ({ ...l, paymentDate: l.paymentDate.slice(0, 10) }))
        .sort((a, b) => a.paymentDate.localeCompare(b.paymentDate))
    : [];
  const upcoming = all.filter((l) => l.paymentDate >= today);
  const past = all.filter((l) => l.paymentDate < today).reverse();

  const renderList = (items: typeof all) => (
    <div className="divide-y rounded-lg border">
      {items.map((launch) => (
        <div key={launch.id} className="flex items-center justify-between px-3 py-2 text-sm">
          <span>
            {formatIsoDate(launch.paymentDate)}
            {launch.id === cost?.id && (
              <span className="ml-2 text-xs text-muted-foreground">(primeiro lançamento)</span>
            )}
          </span>
          <span className="font-medium">{formatCurrency(launch.amount)}</span>
        </div>
      ))}
    </div>
  );

  return (
    <Dialog open={!!cost} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{cost?.title}</DialogTitle>
        </DialogHeader>
        <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
          {isLoading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <p className="text-sm font-semibold">Próximos ({upcoming.length})</p>
                {upcoming.length > 0 ? (
                  renderList(upcoming)
                ) : (
                  <p className="text-sm text-muted-foreground">Nenhum</p>
                )}
              </div>
              <div className="space-y-2">
                <p className="text-sm font-semibold">Anteriores ({past.length})</p>
                {past.length > 0 ? (
                  renderList(past)
                ) : (
                  <p className="text-sm text-muted-foreground">Nenhum</p>
                )}
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Editar recorrência (vale de hoje em diante)
 */
function EditRecurrenceDialog({
  cost,
  onClose,
}: {
  cost: RecurringCost | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<{
    title: string;
    category: OperationalCostCategory;
    amountDisplay: string;
    frequency: RecurrenceFrequency;
    dayOfMonth: number;
    paymentMethod: string;
    description: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Preenche o formulário ao abrir para outra recorrência
  const [loadedId, setLoadedId] = useState<string | null>(null);
  if (cost && cost.id !== loadedId) {
    setLoadedId(cost.id);
    setError(null);
    setForm({
      title: cost.title,
      category: cost.category,
      amountDisplay: formatCurrencyInput(String(Math.round(cost.amount * 100))),
      frequency: cost.frequency,
      dayOfMonth: cost.dayOfMonth,
      paymentMethod: cost.paymentMethod ?? '',
      description: cost.description ?? '',
    });
  }

  const save = useMutation({
    mutationFn: () =>
      operationalCostsAPI.updateRecurrence(cost!.id, {
        title: form!.title,
        category: form!.category,
        amount: parseCurrencyInput(form!.amountDisplay),
        frequency: form!.frequency,
        dayOfMonth: form!.dayOfMonth,
        paymentMethod: form!.paymentMethod || null,
        description: form!.description || null,
        notes: cost!.notes,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['operational-costs'] });
      handleClose();
    },
    onError: (err) => setError(getApiErrorMessage(err, 'Erro ao salvar a recorrência.')),
  });

  const handleClose = () => {
    setLoadedId(null);
    setForm(null);
    onClose();
  };

  const scheduleChanged =
    !!cost && !!form && (form.frequency !== cost.frequency || form.dayOfMonth !== cost.dayOfMonth);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form) return;
    if (parseCurrencyInput(form.amountDisplay) <= 0) {
      setError('Informe um valor maior que zero.');
      return;
    }
    setError(null);
    save.mutate();
  };

  return (
    <Dialog open={!!cost} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Editar recorrência</DialogTitle>
        </DialogHeader>
        {form && (
          <form onSubmit={handleSubmit} className="space-y-4 px-6">
            <Input
              id="rec-title"
              label="Título *"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              required
            />
            <div className="grid grid-cols-2 gap-4">
              <Select
                id="rec-category"
                label="Categoria *"
                value={form.category}
                onChange={(e) =>
                  setForm({ ...form, category: e.target.value as OperationalCostCategory })
                }
                options={categoryOptions}
              />
              <div className="space-y-2">
                <label htmlFor="rec-amount" className="text-sm font-medium">
                  Valor *
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                    R$
                  </span>
                  <Input
                    id="rec-amount"
                    value={form.amountDisplay}
                    onChange={(e) =>
                      setForm({ ...form, amountDisplay: formatCurrencyInput(e.target.value) })
                    }
                    className="pl-10"
                    placeholder="0,00"
                    required
                  />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Select
                id="rec-frequency"
                label="Periodicidade *"
                value={form.frequency}
                onChange={(e) =>
                  setForm({ ...form, frequency: e.target.value as RecurrenceFrequency })
                }
                options={FREQUENCY_OPTIONS}
              />
              <Input
                id="rec-day"
                label="Dia *"
                type="number"
                min="1"
                max="31"
                value={form.dayOfMonth}
                onChange={(e) =>
                  setForm({
                    ...form,
                    dayOfMonth: Math.min(31, Math.max(1, parseInt(e.target.value) || 1)),
                  })
                }
                required
              />
            </div>
            {form.frequency !== 'MONTHLY' && cost && (
              <p className="text-xs text-muted-foreground">
                {describeSchedule(form.frequency, form.dayOfMonth, cost.startDate)} (o mês de
                referência é o do primeiro lançamento, {formatIsoDate(cost.startDate)}).
              </p>
            )}
            <Input
              id="rec-payment-method"
              label="Forma de pagamento"
              value={form.paymentMethod}
              onChange={(e) => setForm({ ...form, paymentMethod: e.target.value })}
            />
            <Textarea
              id="rec-description"
              label="Descrição"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={2}
            />

            <p className="text-xs text-muted-foreground">
              As alterações valem para os lançamentos de hoje em diante. Lançamentos anteriores não
              mudam.
              {scheduleChanged &&
                ' Como a periodicidade ou o dia mudou, os próximos lançamentos serão refeitos.'}
            </p>

            {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

            <DialogFooter>
              <div className="flex gap-2 pt-4">
                <Button type="button" variant="outline" onClick={handleClose}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={save.isPending}>
                  {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Salvar'}
                </Button>
              </div>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Encerrar recorrência (mantém o histórico)
 */
function EndRecurrenceDialog({
  cost,
  onClose,
}: {
  cost: RecurringCost | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [endDate, setEndDate] = useState(todayIso());
  const [error, setError] = useState<string | null>(null);

  const end = useMutation({
    mutationFn: () => operationalCostsAPI.endRecurrence(cost!.id, endDate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['operational-costs'] });
      handleClose();
    },
    onError: (err) => setError(getApiErrorMessage(err, 'Erro ao encerrar a recorrência.')),
  });

  const handleClose = () => {
    setEndDate(todayIso());
    setError(null);
    onClose();
  };

  return (
    <Dialog open={!!cost} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Encerrar recorrência</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 px-6">
          <p className="text-sm text-muted-foreground">
            <strong>{cost?.title}</strong> deixa de gerar lançamentos depois do último dia. Os
            lançamentos até essa data continuam no histórico; os posteriores são removidos.
          </p>
          <Input
            id="end-date"
            label="Último dia *"
            type="date"
            min={todayIso()}
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            required
          />
          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
        </div>
        <DialogFooter>
          <div className="flex gap-2 pt-4">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => end.mutate()}
              disabled={!endDate || end.isPending}
            >
              {end.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Encerrar'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ManageRecurringCostsPage() {
  const navigate = useNavigate();
  const [frequencyFilter, setFrequencyFilter] = useState<'all' | RecurrenceFrequency>('all');
  const [categoryFilter, setCategoryFilter] = useState<'all' | OperationalCostCategory>('all');
  const [statusFilter, setStatusFilter] = useState<'active' | 'ended' | 'all'>('active');
  const [launchesOf, setLaunchesOf] = useState<RecurringCost | null>(null);
  const [editing, setEditing] = useState<RecurringCost | null>(null);
  const [ending, setEnding] = useState<RecurringCost | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['operational-costs', 'recurring'],
    queryFn: async () => {
      const response = await operationalCostsAPI.listRecurring();
      return response.data.data as { items: RecurringCost[]; metrics: RecurringMetrics };
    },
  });

  const items = data?.items ?? [];
  const metrics = data?.metrics;

  // Status e categoria filtram antes; as abas de periodicidade mostram a contagem já filtrada
  const baseFiltered = useMemo(
    () =>
      items.filter(
        (item) =>
          (statusFilter === 'all' ||
            (statusFilter === 'ended' ? item.status === 'ENDED' : item.status !== 'ENDED')) &&
          (categoryFilter === 'all' || item.category === categoryFilter)
      ),
    [items, statusFilter, categoryFilter]
  );
  const visible = baseFiltered.filter(
    (item) => frequencyFilter === 'all' || item.frequency === frequencyFilter
  );
  const countOf = (frequency: RecurrenceFrequency) =>
    baseFiltered.filter((item) => item.frequency === frequency).length;

  const nonMonthly = metrics
    ? (['QUARTERLY', 'SEMIANNUAL', 'YEARLY'] as const).reduce(
        (acc, f) => ({
          count: acc.count + metrics.byFrequency[f].count,
          annualized: acc.annualized + metrics.byFrequency[f].annualized,
        }),
        { count: 0, annualized: 0 }
      )
    : { count: 0, annualized: 0 };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold">Despesas Recorrentes</h1>
          <p className="text-muted-foreground mt-1">
            Mensais, trimestrais, semestrais e anuais em um só lugar
          </p>
        </div>
        <Button variant="outline" onClick={() => navigate('/financial/operational-costs')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar para Despesas
        </Button>
      </div>

      {isLoading || !metrics ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <>
          {/* Métricas (só recorrências ativas) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <MetricCard
              title="Custo mensal equivalente"
              value={formatCurrency(metrics.monthlyEquivalentTotal)}
              subtitle="Mensais + demais divididas pelo intervalo"
              icon={TrendingUp}
            />
            <MetricCard
              title="Custo anualizado"
              value={formatCurrency(metrics.annualizedTotal)}
              subtitle={`${metrics.activeCount} recorrências ativas em 12 meses`}
              icon={CalendarRange}
            />
            <MetricCard
              title="Mensais"
              value={`${formatCurrency(metrics.byFrequency.MONTHLY.amount)}/mês`}
              subtitle={`${metrics.byFrequency.MONTHLY.count} despesas`}
              icon={Repeat}
            />
            <MetricCard
              title="Trimestrais, semestrais e anuais"
              value={`${formatCurrency(nonMonthly.annualized)}/ano`}
              subtitle={`${nonMonthly.count} despesas`}
              icon={CalendarClock}
            />
          </div>

          {/* Recorrências */}
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-4">
                {/* Filtros */}
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  <TabFilter
                    tabs={[
                      { value: 'all', label: 'Todas', count: baseFiltered.length },
                      ...FREQUENCY_OPTIONS.map((o) => ({
                        value: o.value,
                        label: FREQUENCY_PLURAL_LABELS[o.value],
                        count: countOf(o.value),
                      })),
                    ]}
                    currentTab={frequencyFilter}
                    onChange={(value) => setFrequencyFilter(value as typeof frequencyFilter)}
                  />
                  <div className="flex gap-3">
                    <div className="w-48">
                      <Select
                        id="category-filter"
                        value={categoryFilter}
                        onChange={(e) => setCategoryFilter(e.target.value as typeof categoryFilter)}
                        options={[
                          { value: 'all', label: 'Todas as categorias' },
                          ...categoryOptions,
                        ]}
                      />
                    </div>
                    <div className="w-40">
                      <Select
                        id="status-filter"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
                        options={[
                          { value: 'active', label: 'Ativas' },
                          { value: 'ended', label: `Encerradas (${metrics.endedCount})` },
                          { value: 'all', label: 'Todas' },
                        ]}
                      />
                    </div>
                  </div>
                </div>

                {/* Tabela */}
                {visible.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    <p>Nenhuma despesa recorrente encontrada.</p>
                    {items.length === 0 && (
                      <Button
                        className="mt-4"
                        onClick={() => navigate('/financial/operational-costs')}
                      >
                        Criar despesa recorrente
                      </Button>
                    )}
                  </div>
                ) : (
                  <SortableTable data={visible} defaultSortKey="title" defaultSortDirection="asc">
                    {(sorted) => (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead sortable sortKey="title">
                              Despesa
                            </TableHead>
                            <TableHead sortable sortKey="frequency">
                              Periodicidade
                            </TableHead>
                            <TableHead sortable sortKey="amount" align="right">
                              Valor
                            </TableHead>
                            <TableHead sortable sortKey="monthlyEquivalent" align="right">
                              Equiv. mensal
                            </TableHead>
                            <TableHead sortable sortKey="nextDueDate">
                              Próximo
                            </TableHead>
                            <TableHead align="right">Ações</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {sorted.map((cost) => (
                            <TableRow
                              key={cost.id}
                              className={cost.status === 'ENDED' ? 'opacity-60' : undefined}
                            >
                              <TableCell>
                                <p className="font-medium">{cost.title}</p>
                                <p className="text-xs text-muted-foreground">
                                  {categoryLabels[cost.category]}
                                </p>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <span
                                    className={`text-xs px-2 py-1 rounded font-medium whitespace-nowrap ${FREQUENCY_BADGE_CLASSES[cost.frequency]}`}
                                  >
                                    {FREQUENCY_LABELS[cost.frequency]}
                                  </span>
                                  <span className="text-xs text-muted-foreground">
                                    {describeSchedule(
                                      cost.frequency,
                                      cost.dayOfMonth,
                                      cost.startDate
                                    )}
                                  </span>
                                </div>
                              </TableCell>
                              <TableCell className="text-right font-medium">
                                {formatCurrency(cost.amount)}
                              </TableCell>
                              <TableCell className="text-right text-muted-foreground">
                                {FREQUENCY_INTERVAL_MONTHS[cost.frequency] === 1
                                  ? '—'
                                  : formatCurrency(cost.monthlyEquivalent)}
                              </TableCell>
                              <TableCell>
                                {cost.status === 'ENDED' ? (
                                  <span className="text-xs text-muted-foreground">
                                    Encerrada em {formatIsoDate(cost.endedAt!)}
                                  </span>
                                ) : (
                                  <div>
                                    <span>
                                      {cost.nextDueDate ? formatIsoDate(cost.nextDueDate) : '—'}
                                    </span>
                                    {cost.status === 'ENDING' && (
                                      <p className="text-xs text-orange-600 dark:text-orange-400">
                                        Encerra em {formatIsoDate(cost.endedAt!)}
                                      </p>
                                    )}
                                  </div>
                                )}
                              </TableCell>
                              <TableCell>
                                <div className="flex justify-end gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setLaunchesOf(cost)}
                                    aria-label="Ver lançamentos"
                                    title="Ver lançamentos"
                                  >
                                    <List className="h-4 w-4" />
                                  </Button>
                                  {cost.status !== 'ENDED' && (
                                    <>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => setEditing(cost)}
                                        aria-label="Editar recorrência"
                                        title="Editar"
                                      >
                                        <Pencil className="h-4 w-4" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => setEnding(cost)}
                                        aria-label="Encerrar recorrência"
                                        title="Encerrar"
                                      >
                                        <Square className="h-4 w-4 text-red-600" />
                                      </Button>
                                    </>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </SortableTable>
                )}
              </div>
            </CardContent>
          </Card>
        </>
      )}

      <LaunchesDialog cost={launchesOf} onClose={() => setLaunchesOf(null)} />
      <EditRecurrenceDialog cost={editing} onClose={() => setEditing(null)} />
      <EndRecurrenceDialog cost={ending} onClose={() => setEnding(null)} />
    </div>
  );
}
